import { selectTac } from '../tac/tacSelectors';
import {
  selectCurrentConfiguration,
  selectSceneImage,
  selectShoppingItems,
} from '../summary/summarySelectors';
import {
  selectSaveVpcProgressStatus,
  selectSavedVpcCode,
} from './vpcSelectors';
import { SummaryPageConfirmationCardTypeEnum } from '@inter-ikea-kompis/component-summary-page';
import { ConfirmationSummaryShareDesignStateEnum } from '@inter-ikea-kompis/component-configuration-summary';
import { DesignInteractionEnum } from '@insights/insights-data-provider';
import { ShareDesignCardStateEnum } from '@inter-ikea-kompis/component-share-design-card';
import { actionSetCurrentConfiguration } from '../summary/summaryActions';
import { getICF } from '../../util/aactools/ICF';
import convert from '../../util/aactools/convert';
import {
  actionSetDirtyConfiguration,
  actionSetSavedVpcCode,
  actionSetSaveVpcState,
  actionSetVpcSaveProgressStatus,
} from './vpcActions';
import { actionSetOverlayState } from '../popups/popupsActions';
import { VpcSaveProgressStatusEnum } from './vpcTypes';
import { getVpcService } from '../../services/ServiceHandler';
import {
  confirmationModal,
  shareDesignModal,
  thunkSetSummaryInstanceState,
  thunkResetSavedVpcAndDesignLink,
  thunkSaveVpcAndGenerateDesignLink,
} from '../summary/summaryThunks';
import mandatoryStatisticsReporter from '../../services/statistics/insights/mandatory/mandatoryStatisticsReporter';

/**
 * Reset saved VPC
 */
export const thunkResetSavedVpc = () => (dispatch: any, getState: any) => {
  dispatch(actionSetVpcSaveProgressStatus(VpcSaveProgressStatusEnum.default));
  dispatch(actionSetSavedVpcCode(''));
  dispatch(actionSetDirtyConfiguration(true));
};

/**
 * Save VPC
 */
export const thunkSaveVpc = () => async (dispatch: any, getState: any) => {
  const state = getState();
  const shoppingItems = selectShoppingItems(state);
  const tac = selectTac(getState());

  dispatch(actionSetVpcSaveProgressStatus(VpcSaveProgressStatusEnum.saving));
  dispatch(actionSetCurrentConfiguration(convert.TACtoPAC(tac)));
  const vpcConfiguration =
    tac && (await dispatch(thunkSaveVpcConfig(shoppingItems)));
  dispatch(actionSetSavedVpcCode(vpcConfiguration?.configurationId || ''));

  if (vpcConfiguration) {
    dispatch(actionSetCurrentConfiguration(vpcConfiguration));
    dispatch(actionSetDirtyConfiguration(false));
    dispatch(actionSetVpcSaveProgressStatus(VpcSaveProgressStatusEnum.success));

    mandatoryStatisticsReporter.reportDesignInteractionEvent(
      vpcConfiguration.configurationId,
      DesignInteractionEnum.storeConfiguration
    );
  } else {
    dispatch(actionSetVpcSaveProgressStatus(VpcSaveProgressStatusEnum.failure));
  }

  return vpcConfiguration;
};

/**
 * Save vpc config
 * @param shoppingItems
 */
const thunkSaveVpcConfig =
  (shoppingItems: any) => async (dispatch: any, getState: any) => {
    const state = getState();
    const tac = selectTac(state);
    const currentConfiguration = selectCurrentConfiguration(state);

    if (!tac)
      throw new Error('No tac existed while trying to save Vpc config.');

    const icf: any = getICF(tac);
    let configuration;
    try {
      configuration = await getVpcService().saveConfiguration(
        currentConfiguration,
        shoppingItems,
        selectSceneImage(state),
        icf
      );
    } catch (e) {
      configuration = null;
    }

    return configuration;
  };

/* If VPC has not yet been successfully saved, make a new
   attempt to save VPC + generate design link. Returns an
   object containing the new VPC saved progress status and
   the new saved VPC code if a retry was needed, otherwise
   just returns an object containing the old values. */
export const thunkRetrySaveVpcIfNeeded =
  () => async (dispatch: any, getState: any) => {
    const readCurrentState = () => {
      const state = getState();
      const currentState = {
        saveVpcProgressStatus: selectSaveVpcProgressStatus(state),
        savedVpcCode: selectSavedVpcCode(state),
      };

      return currentState;
    };

    const currentState = readCurrentState();

    if (
      currentState.saveVpcProgressStatus !== VpcSaveProgressStatusEnum.success
    ) {
      dispatch(thunkResetSavedVpcAndDesignLink());
      await dispatch(thunkSaveVpcAndGenerateDesignLink());

      const stateAfterRetry = readCurrentState();
      currentState.saveVpcProgressStatus =
        stateAfterRetry.saveVpcProgressStatus;
      currentState.savedVpcCode = stateAfterRetry.savedVpcCode;
    }

    return currentState;
  };

/**
 * On user interaction to share design.
 */
export const thunkOnShareDesign =
  () => async (dispatch: any, getState: any) => {
    const { saveVpcFail: saveVpcFailCard } =
      SummaryPageConfirmationCardTypeEnum;
    const { default: defaultShareDesignCard } = ShareDesignCardStateEnum;

    /**
     * On failed share design
     */
    const onShareDesignFail = () => {
      dispatch(actionSetOverlayState(true));
      dispatch(
        thunkSetSummaryInstanceState(confirmationModal, {
          confirmationCard: saveVpcFailCard,
        })
      );
    };

    /**
     * On successful share design
     */
    const onShareDesignSuccess = () => {
      dispatch(
        thunkSetSummaryInstanceState(shareDesignModal, {
          shareDesignCard: defaultShareDesignCard,
        })
      );
      dispatch(actionSetOverlayState(true));
    };

    dispatch(
      actionSetSaveVpcState(ConfirmationSummaryShareDesignStateEnum.loading)
    );

    // Retry VPC save if needed. If not needed, we'll get the previous values.
    const { saveVpcProgressStatus } = await dispatch(
      thunkRetrySaveVpcIfNeeded()
    );

    dispatch(
      actionSetSaveVpcState(ConfirmationSummaryShareDesignStateEnum.default)
    );

    switch (saveVpcProgressStatus) {
      case VpcSaveProgressStatusEnum.success:
        onShareDesignSuccess();
        break;
      case VpcSaveProgressStatusEnum.failure:
        onShareDesignFail();
        break;
      default:
        // This should never happen, but treat it as a failure should it somehow happen.
        // Add a statistics event, so that it's reported to us?
        onShareDesignFail();
    }
  };
